import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Close } from '@codex-brightworld/organisms';
import smoothScroll from '../../helpers/smoothScroll';
import uiLocale from '../../../../../utils/UILocale.js';

// Import all child components need for PSA variant one
import ProductInfo from '../ProductInfo/ProductInfo';
import ActionButtons from '../ActionButtons/ActionButtons';
import AttachmentListHeader from '../AttachmentsListHeader/AttachmentsListHeader';
import AttachmentsListTabs from '../AttachmentsListTabs/AttachmentsListTabs';
import AttachmentBlock from '../AttachmentBlock/AttachmentBlock';
import { AddToBasketErrorMessage } from '../AddToBasketErrorMessage/AddToBasketErrorMessage';
import CollectionFrom from '../CollectionFrom/CollectionFrom';

const translations = {
  failedToUpdateBasket: uiLocale.getString('component.psa.failed_to_update_basket') ||
  'Failed to update basket',
  pleaseTryAgain: uiLocale.getString('component.psa.please_try_again') ||
  'Please try again'
};

const StyledContainer = styled.div`
  position: relative;
  width: 440px;
  max-width: 440px;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const StyledClose = styled(Close)`
  position: absolute;
  top: 15px;
  right: 27px;
  z-index: 1;
  width: 10px;
  height: 10px;
  color: ${({ theme }) => theme.colors.link.default};
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    right: 19px;
  }
  
  > span {
    > svg {
      > g {
        /* Hide border of the icon */
        path:nth-child(1) {
          display: none
        }
      }
    }
  }
`;

const TopBlockWrapper = styled.div`
  padding: 56px 19px 2px 19px;
  background: #fff;
  position: relative;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 53px 10px 2px 10px;
  }
`;

const ProductInfoWrapper = styled.div`
  padding-bottom: 16px;
`;

const TabsWrapper = styled.div`
  background: #fff;
  padding: 10px 20px 10px 20px;

  @media ${({ theme }) => theme.breakpoints.mobile} {
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const AttachmentWrapper = styled.div`
  background: #fff;
  margin-bottom: 32px;
  padding: 16px;
  border-radius: 10px;
  border: solid 1px #cdd8df;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    margin-bottom: 24px;
  }
`;

const ScrollWrapper = styled.div`
  background-color: ${(props) => props.theme.colors.shades[6]};
  padding: 10px 9px 0 9px;
  overflow-y: scroll;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 10px 5px;
  }
`;

const BorderWrapper = styled.div`
  background-color: #f2f2f2;
  overflow: hidden;
  padding: 10px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 10px 5px;
  }
`;

const StyledAttachmentsListTabs = styled(AttachmentsListTabs)`
  box-shadow: 0 4px 2px -2px rgba(143,143,143,1);
`;

const WrapperVariant1 = ({
  psaWhenBoughtWithEnabled,
  productName,
  productManufacturer,
  productImgUrl,
  productPrice,
  productID,
  storeId,
  deliveryType,
  isProductBundle,
  subproductNames,
  attachments,
  attachmentsLabel,
  continueToBasketButtonLabel,
  continueShoppingButtonLabel,
  addAttachmentInBasketLabel,
  addAttachmentInBasketInProgressLabel,
  attachmentAddedInBasketLabel,
  continueToBasketButtonAction,
  continueShoppingButtonAction,
  onAddToBasket,
  onAddToBasketSuccess,
  onAddToBasketFailure,
  productIdForFailedAddToBasket,
  headerItemLabel,
  headerBundleLabel,
  displayCollectionFrom,
  collectionFromLabel,
  collectionFromPlace,
  collectionFromDelivery,
  storeUrl
}) => {
  const [activeAttachmentId, handleRef] = useState(null);
  //  reference array to all attachments
  const parentContainerRef = useRef(null);
  const attachmentRefs = useRef([...Array(5)].map(() => React.createRef()));
  const firstAttachmentId = attachments && attachments[0] && attachments[0].id.toString();
  const displayTabs = false;
  const failedBasketText = () => {
    const { failedToUpdateBasket, pleaseTryAgain } = translations;
    return (
      <AddToBasketErrorMessage
        failedToUpdateBasket={failedToUpdateBasket}
        pleaseTryAgain={pleaseTryAgain}
      />
    );
  };

  //  hook for scrolling onto active attachment
  useEffect(() => {
    smoothScroll(attachmentRefs, parentContainerRef, activeAttachmentId);
  }, [activeAttachmentId]);

  let productTitle = productName;
  if (productName.indexOf(productManufacturer) === -1) {
    productTitle = `${productManufacturer} ${productName}`;
  }

  return (
    <>
      <StyledContainer ref={parentContainerRef}>
        <StyledClose onClose={continueShoppingButtonAction} />
        <TopBlockWrapper>
          <ProductInfoWrapper>
            <ProductInfo
              productImgUrl={productImgUrl}
              productTitle={productTitle}
              productPrice={productPrice}
              translation={isProductBundle ? headerBundleLabel : headerItemLabel}
              bundle={{
                isExpanded: false,
                items: subproductNames
              }}
            />
          </ProductInfoWrapper>
          <ActionButtons
            primaryButtonLabel={continueToBasketButtonLabel}
            primaryButtonOnClick={continueToBasketButtonAction}
            secondaryButtonLabel={continueShoppingButtonLabel}
            secondaryButtonOnClick={continueShoppingButtonAction}
          />
        </TopBlockWrapper>
        {displayTabs && (
          <TabsWrapper>
            {attachments ? (
              <StyledAttachmentsListTabs
                attachments={attachments}
                activeAttachmentID={activeAttachmentId || firstAttachmentId}
                switchTab={(current) => {
                  handleRef(current);
                }}
              />
            ) : (
              <StyledAttachmentsListTabs
                attachments={[{}, {}, {}, {}, {}]}
                switchTab={(current) => {
                  handleRef(current);
                }}
                isLoading
              />
            )}
          </TabsWrapper>
        )}
        <ScrollWrapper>
          {displayCollectionFrom && (
          <CollectionFrom
            label={collectionFromLabel}
            place={collectionFromPlace}
            delivery={collectionFromDelivery}
          />
          )}
          <BorderWrapper>
            {attachmentsLabel && <AttachmentListHeader label={attachmentsLabel} />}
            {attachments.map((item, actual) => (
              <AttachmentWrapper key={item.id} ref={attachmentRefs.current[actual]}>
                {item.id === productIdForFailedAddToBasket &&
                failedBasketText()}
                {item && (
                <AttachmentBlock
                  productID={productID}
                  attachmentName={item.name}
                  attachmentBrandLabel={item.brandLabel}
                  attachmentImgUrl={item.imageUrl}
                  ratingCount={item.customerReview.number}
                  ratingValue={item.customerReview.averageScore}
                  currentPrice={item.price.amount}
                  wasPrice={item.wasPrice}
                  buyTogetherPrice={psaWhenBoughtWithEnabled
                    ? item.bundlePrice && item.bundlePrice.priceInBundle : null}
                  currencyCode={item.price.currencyCode}
                  addAttachmentInBasketFupid={item.id}
                  addAttachmentInBasketQuantity={1}
                  storeId={storeId}
                  deliveryType={deliveryType}
                  addAttachmentInBasketLabel={addAttachmentInBasketLabel}
                  inProgressLabel={addAttachmentInBasketInProgressLabel}
                  attachmentAddedInBasketLabel={attachmentAddedInBasketLabel}
                  buyTogetherSavePrice={item.buyTogetherSavePrice}
                  onAddToBasket={() => onAddToBasket(productID, item.id)}
                  onAddToBasketSuccess={onAddToBasketSuccess}
                  onAddToBasketFailure={onAddToBasketFailure}
                  psaWhenBoughtWithEnabled={psaWhenBoughtWithEnabled}
                  storeUrl={storeUrl}
                />
                )}
              </AttachmentWrapper>
            ))}
          </BorderWrapper>
        </ScrollWrapper>
      </StyledContainer>
    </>
  );
};

WrapperVariant1.propTypes = {
  psaWhenBoughtWithEnabled: PropTypes.bool,
  productName: PropTypes.string.isRequired,
  productManufacturer: PropTypes.string.isRequired,
  productImgUrl: PropTypes.string.isRequired,
  productPrice: PropTypes.number.isRequired,
  productID: PropTypes.string.isRequired,
  storeId: PropTypes.number,
  deliveryType: PropTypes.oneOf(['RESERVE_AND_COLLECT', 'PAY_AND_COLLECT', 'PAY_AND_COLLECT_FROM_STORE_STOCK']),
  isProductBundle: PropTypes.bool.isRequired,
  subproductNames: PropTypes.arrayOf(PropTypes.string),
  attachments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    brandLabel: PropTypes.string,
    imageUrl: PropTypes.string,
    price: PropTypes.shape({
      amount: PropTypes.number,
      currencyCode: PropTypes.string
    }),
    wasPrice: PropTypes.shape({
      amount: PropTypes.number,
      discountAmount: PropTypes.number
    }),
    buyTogetherPrice: PropTypes.number,
    customerReview: PropTypes.shape({
      averageScore: PropTypes.number,
      number: PropTypes.number
    })
  })).isRequired,
  attachmentsLabel: PropTypes.string.isRequired,
  continueToBasketButtonLabel: PropTypes.string.isRequired,
  continueShoppingButtonLabel: PropTypes.string.isRequired,
  addAttachmentInBasketLabel: PropTypes.string.isRequired,
  addAttachmentInBasketInProgressLabel: PropTypes.string.isRequired,
  attachmentAddedInBasketLabel: PropTypes.string.isRequired,
  continueToBasketButtonAction: PropTypes.func.isRequired,
  continueShoppingButtonAction: PropTypes.func.isRequired,
  onAddToBasket: PropTypes.func,
  onAddToBasketSuccess: PropTypes.func,
  onAddToBasketFailure: PropTypes.func,
  productIdForFailedAddToBasket: PropTypes.string,
  headerItemLabel: PropTypes.string,
  headerBundleLabel: PropTypes.string,
  displayCollectionFrom: PropTypes.bool,
  collectionFromLabel: PropTypes.string,
  collectionFromPlace: PropTypes.string,
  collectionFromDelivery: PropTypes.bool,
  storeUrl: PropTypes.string.isRequired
};

WrapperVariant1.defaultProps = {
  psaWhenBoughtWithEnabled: false,
  productIdForFailedAddToBasket: '',
  onAddToBasket: () => {},
  onAddToBasketSuccess: () => {},
  onAddToBasketFailure: () => {},
  headerItemLabel: '',
  headerBundleLabel: '',
  subproductNames: [],
  storeId: undefined,
  deliveryType: undefined,
  displayCollectionFrom: false,
  collectionFromLabel: '',
  collectionFromPlace: '',
  collectionFromDelivery: false
};

export default WrapperVariant1;
